import React, { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import cn from 'classnames';
import css from 'components/Admin/AttendanceDetails.module.scss';
import Dialog from 'components/Dialog';
import scss from 'components/shared.module.scss';
import { Button, LoaderSpinner } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { DATE_FORMAT, TIME_FORMAT } from 'globals/constants';
import { RootState } from 'redux/reducers';
import { useClassAttendanceAPI } from 'services/course-api';

import ExportAttendance from './ExportAttendance';
import RemoveExportedActivityDialog from './RemoveExportedActivityDialog';

interface ClassAttendanceDetailsProps {
  isOpen: boolean;
  getAccess: SessionData | null;
  session: ClassAverageAttendance | null;
  onClose(hasExported: boolean): void;
}

const getAttendanceStatus = (s: StudentAttendance) => {
  switch (s.status) {
    case 'present':
      return {
        style: 'table__cell-value--green',
        label: 'Present',
      };
    case 'excused':
      return {
        style: 'table__cell-value--orange',
        label: 'Excused',
      };
    case 'late':
      return {
        style: 'table__cell-value--orange',
        label: 'Late',
      };
    case 'absent':
      return {
        style: 'table__cell-value--red',
        label: 'Absent',
      };
    case 'checkedIn':
      return {
        style: 'table__cell-value--dark-grey',
        label: 'Checked In',
      };
    default:
      return {
        style: 'table__cell-value--light-grey',
        label: '--',
      };
  }
};

const ClassAttendanceDetails: FC<ClassAttendanceDetailsProps> = ({ isOpen, session, onClose, getAccess }) => {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isRemoveExportedActivityDialogOpen, setRemoveExportedActivityDialogOpen] = useState(false);

  const openRemoveExportedActivityDialog = () => {
    setRemoveExportedActivityDialogOpen(true);
  };
  const closeRemoveExportedActivityDialog = () => {
    setRemoveExportedActivityDialogOpen(false);
  };

  const handleRemoveExport = () => {
    if (!session) return;
    onClose(false);
    closeRemoveExportedActivityDialog();
  };

  const { data: studentPayload = null } = useClassAttendanceAPI(
    getAccess?.courseDetails.courseId,
    session?.classId
  );

  const presentStudents = useMemo(
    () => (studentPayload ? studentPayload.studentsData.filter((s) => s.status === 'present') : []),
    [studentPayload]
  );

  const absentStudents = useMemo(
    () =>
      studentPayload
        ? studentPayload.studentsData.filter(
            (s) => s.status === 'absent' && studentPayload.attendanceRecorded
          )
        : [],
    [studentPayload]
  );

  const lateStudents = useMemo(
    () => (studentPayload ? studentPayload.studentsData.filter((s) => s.status === 'late') : []),
    [studentPayload]
  );

  const excusedStudents = useMemo(
    () => (studentPayload ? studentPayload.studentsData.filter((s) => s.status === 'excused') : []),
    [studentPayload]
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title={
        <div className={css['class-attendance-details']}>
          <div className={css.number}>Session {session?.classNum}</div>
          <div className={css.title}>{session?.title}</div>
        </div>
      }
      footer={
        session?.exported ? (
          <div className={css['footer']}>
            <span>Already Exported</span>
            <Button variant='outlined' color='error' size='large' onClick={openRemoveExportedActivityDialog}>
              Remove Exported Data
            </Button>
          </div>
        ) : (
          <Button variant='outlined' color='info' size='large' onClick={() => setShowExportDialog(true)}>
            Export Attendance Data
          </Button>
        )
      }
    >
      {studentPayload === null ? (
        <div className={scss['loader-wrapper']}>
          <LoaderSpinner />
        </div>
      ) : (
        <div className={css['class-attendance-details-body']}>
          <div className={css.header}>
            <div className={css['header-row']}>
              <div>{dt.format(studentPayload.startTime, DATE_FORMAT)}</div>
              <div>
                {presentStudents.length} present, {lateStudents.length} late, {excusedStudents.length} excused
              </div>
            </div>
            <div className={css['header-row']}>
              <div>
                {dt.format(studentPayload.startTime, TIME_FORMAT)} -{' '}
                {dt.format(studentPayload.endTime, TIME_FORMAT)}
              </div>
              <div>{absentStudents.length} absent</div>
            </div>
          </div>
          <table className='table'>
            <thead>
              <tr>
                <th className='table__cell table__cell--left'>Student Name</th>
                <th className='table__cell table__cell--left'>Email</th>
                <th className='table__cell table__cell--center'>Status</th>
              </tr>
            </thead>
            <tbody>
              {studentPayload.studentsData.map((s) => {
                const status = getAttendanceStatus(s);
                return (
                  <tr key={s.userId}>
                    <td className='table__cell table__cell--left'>{s.name}</td>
                    <td className='table__cell table__cell--left'>{s.emailId}</td>
                    <td className={cn('table__cell table__cell--center', status.style)}>{status.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ExportAttendance
        isOpen={showExportDialog}
        classId={session?.classId}
        onClose={(hasExported) => {
          setShowExportDialog(false);
          onClose(hasExported);
        }}
        attendanceDetails={studentPayload}
      />
      <RemoveExportedActivityDialog
        isOpen={isRemoveExportedActivityDialogOpen}
        selectedActivity={
          session
            ? {
                ...session,
                activityId: session.classId,
                type: 'attendance',
              }
            : null
        }
        onClose={closeRemoveExportedActivityDialog}
        onExport={handleRemoveExport}
      />
    </Dialog>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    getAccess: state.getAccess,
  };
};

export default connect(mapStateToProps)(ClassAttendanceDetails);
